import { useCallback, useEffect, useState } from 'react';
import {
  AccessRequestItem,
  AccessRole,
  Organization,
  approveAccessRequest,
  declineAccessRequest,
  fetchAccessRequests,
} from '../../../api/accessApi';
import { formatTime, useAsync } from './shared';

export default function RequestsTab({
  roles,
  orgs,
  myLevel,
  onChanged,
}: {
  roles: AccessRole[];
  orgs: Organization[];
  myLevel: number;
  onChanged: () => void;
}) {
  const [filter, setFilter] = useState<AccessRequestItem['status']>('pending');
  const [items, setItems] = useState<AccessRequestItem[]>([]);
  const [choice, setChoice] = useState<Record<number, { role: string; org: string }>>({});
  const { error, busy, run } = useAsync();

  const assignable = roles.filter((r) => r.level < myLevel);

  const load = useCallback(
    () => run(() => fetchAccessRequests(filter)).then((d) => d && setItems(d.requests)),
    [run, filter],
  );
  useEffect(() => {
    load();
  }, [load]);

  const pick = (id: number) => choice[id] ?? { role: '', org: '' };

  const approve = async (r: AccessRequestItem) => {
    const { role, org } = pick(r.id);
    const done = await run(() => approveAccessRequest(r.id, Number(role), org ? Number(org) : null));
    if (done) {
      await load();
      onChanged();
    }
  };

  const decline = async (r: AccessRequestItem) => {
    const note = window.prompt(`Decline ${r.username}? Optional reason:`);
    if (note === null) return; // cancelled
    const done = await run(() => declineAccessRequest(r.id, note));
    if (done) {
      await load();
      onChanged();
    }
  };

  return (
    <section className="panel">
      <div className="acc-bar">
        <h2>Access requests</h2>
        <div className="filters">
          {(['pending', 'approved', 'declined'] as const).map((s) => (
            <button key={s} className="chip" aria-pressed={filter === s} onClick={() => setFilter(s)}>
              {s[0].toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>
      {error && <p className="acc-error">{error}</p>}
      <div className="acc-scroll">
        <table className="acc-table">
          <thead>
            <tr>
              <th>Requester</th>
              <th>Company</th>
              <th>Department</th>
              <th>Requested</th>
              <th>{filter === 'pending' ? 'Grant access' : 'Outcome'}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id}>
                <td>
                  <b>{r.full_name}</b>
                  <span>
                    {r.username} · {r.email}
                  </span>
                </td>
                <td>{r.company || '–'}</td>
                <td>{r.department || '–'}</td>
                <td>{formatTime(r.created_at)}</td>
                <td>
                  {r.status === 'pending' ? (
                    <div className="acc-actions acc-wrap">
                      <select
                        aria-label={`Role for ${r.username}`}
                        value={pick(r.id).role}
                        onChange={(e) => setChoice({ ...choice, [r.id]: { ...pick(r.id), role: e.target.value } })}
                      >
                        <option value="">Role…</option>
                        {assignable.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                      <select
                        aria-label={`Organization for ${r.username}`}
                        value={pick(r.id).org}
                        onChange={(e) => setChoice({ ...choice, [r.id]: { ...pick(r.id), org: e.target.value } })}
                      >
                        <option value="">No organization</option>
                        {orgs.filter((o) => o.is_active).map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.name}
                          </option>
                        ))}
                      </select>
                      <button className="btn primary" disabled={busy || !pick(r.id).role} onClick={() => approve(r)}>
                        Approve
                      </button>
                      <button className="btn" disabled={busy} onClick={() => decline(r)}>
                        Decline
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="tag">{r.status}</span>
                      <span>
                        by {r.reviewed_by ?? 'unknown'} · {formatTime(r.reviewed_at)}
                        {r.review_note ? ` · ${r.review_note}` : ''}
                      </span>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && !busy && <p className="note">No {filter} requests.</p>}
      </div>
    </section>
  );
}
